import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Typography,
  Box,
  LinearProgress
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import authService from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

const SessionTimeoutWarning = () => {
  const { isAuthenticated, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const checkSessionStatus = () => {
      const userData = authService.getUserData();
      if (!userData?.loginTime) {
        return;
      }

      const loginTime = new Date(userData.loginTime);
      const now = new Date();
      const elapsed = now - loginTime;
      const threeHours = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
      const remaining = threeHours - elapsed;

      // Show warning 5 minutes before expiry
      const fiveMinutes = 5 * 60 * 1000;
      
      if (remaining <= fiveMinutes && remaining > 0) {
        setTimeRemaining(Math.ceil(remaining / 1000)); // Convert to seconds
        setShowWarning(true);
        setOpen(true);
      } else if (remaining <= 0) {
        // Session expired
        handleSessionExpired();
      }
    };

    // Check immediately
    checkSessionStatus();

    // Check every 30 seconds
    const interval = setInterval(checkSessionStatus, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  useEffect(() => {
    if (showWarning && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleSessionExpired();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [showWarning, timeRemaining]);

  const handleSessionExpired = () => {
    setOpen(false);
    setShowWarning(false);
    authService.handleSessionExpiry();
  };

  const handleExtendSession = () => {
    // Extend the session by 30 minutes
    const extended = authService.extendSession();
    
    if (extended) {
      console.log('Session extended by 30 minutes');
      // Close the dialog and reset the warning state
      setOpen(false);
      setShowWarning(false);
      setTimeRemaining(0);
    } else {
      console.error('Failed to extend session');
      // If extension failed, still close the dialog but the session will expire naturally
      setOpen(false);
      setShowWarning(false);
    }
  };

  const handleLogoutNow = () => {
    setOpen(false);
    setShowWarning(false);
    logout();
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (!isAuthenticated || !showWarning) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleExtendSession}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6">Session Timeout Warning</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Your session will expire in <strong>{formatTime(timeRemaining)}</strong> due to inactivity.
        </DialogContentText>
        <Box sx={{ mt: 2, mb: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={((5 * 60) - timeRemaining) / (5 * 60) * 100}
            color="warning"
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          You will be automatically logged out when the timer reaches zero for security reasons.
        </Typography>
        <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
          Click "Stay Logged In" to extend your session by 30 minutes.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleLogoutNow} color="secondary">
          Logout Now
        </Button>
        <Button onClick={handleExtendSession} variant="contained" color="primary">
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutWarning;